<template>
  <div class="bg-white rounded-2xl shadow p-6">
    <div class="rounded-2xl p-6">
      <h2 class="text-center font-bold mb-4">Capacidades Operacionais</h2>
      <canvas ref="radarChart"></canvas>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, toRefs } from 'vue'
import {
  Chart,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js'
import { getOperationalCapabilitiesByIdEnterprise } from '@/services/graphs/api.operational_capabilities'

Chart.register(
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
)

const radarChart = ref(null)
const chartInstance = ref(null)

const props = defineProps({
  enterpriseId: { type: String, required: true },
})

const { enterpriseId } = toRefs(props)

function getCapabilityData(data, field) {
  return {
    Implemented: data[`${field}Implemented`] || 0,
    PartiallyImplemented: data[`${field}PartiallyImplemented`] || 0,
    NotApplicable: data[`${field}NotApplicable`] || 0,
  }
}

async function loadData(id) {
  try {
    const dataArray = await getOperationalCapabilitiesByIdEnterprise(id)
    if (!dataArray || !dataArray.length) {
      chartInstance.value?.destroy()
      chartInstance.value = null
      return
    }

    const data = dataArray[0]

    const capacidades = [
      'Governance',
      'AssetManagement',
      'InformationProtection',
      'HumanResourceSecurity',
      'PhysicalSecurity',
      'NetworkSystemsSecurity',
      'ApplicationSecurity',
      'SecureConfiguration',
      'IdentityAndAccessManagement',
      'ThreatAndVulnerabilityManagement',
      'BusinessContinuity',
      'SupplierRelationshipSecurity',
      'InformationSecurityEventManagement',
      'InformationSecurityAssurance',
      'LegalAndCompliance',
    ]

    const colors = ['#22c55e', '#facc15', '#ef4444']
    const levels = [
      { key: 'Implemented', label: 'Implementados' },
      { key: 'PartiallyImplemented', label: 'Parciais' },
      { key: 'NotApplicable', label: 'Não Implementados' },
    ]

    const datasets = levels.map((level, i) => ({
      label: level.label,
      data: capacidades.map((cap) => getCapabilityData(data, cap)[level.key]),
      fill: true,
      backgroundColor: colors[i],
      borderColor: colors[i],
      pointBackgroundColor: colors[i],
      pointBorderColor: '#fff',
      borderWidth: 2,
    }))

    if (chartInstance.value) {
      chartInstance.value.data.labels = capacidades
      chartInstance.value.data.datasets = datasets
      chartInstance.value.update()
    } else {
      chartInstance.value = new Chart(radarChart.value, {
        type: 'radar',
        data: {
          labels: capacidades,
          datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            r: {
              angleLines: { display: true },
              suggestedMin: 0,
              suggestedMax: 10,
            },
          },
        },
      })
    }
  } catch (error) {
    console.error('Erro ao carregar capacidades operacionais:', error)
    chartInstance.value?.destroy()
    chartInstance.value = null
  }
}

onMounted(() => loadData(enterpriseId.value))
watch(enterpriseId, (newId) => loadData(newId))
</script>
